import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // ProSetup: keep the native cart icon + drawer in sync after the custom
    // add-to-cart flow (snippets/prosetup-cart-ux.liquid), which adds via its
    // own AJAX and dispatches 'cart:item-added' instead of Horizon's events.
    document.addEventListener('cart:item-added', this.#handlePsCartSync);
    document.addEventListener('cart:refresh', this.#handlePsCartSync);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#applyPsCartTweaks();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:item-added', this.#handlePsCartSync);
    document.removeEventListener('cart:refresh', this.#handlePsCartSync);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  /**
   * ProSetup: rename the drawer checkout button to 'Passer la commande'.
   */
  #applyPsCartTweaks() {
    this.querySelectorAll('.cart__checkout-button .button-text').forEach((el) => {
      el.textContent = 'Passer la commande';
    });
  }

  /**
   * ProSetup: after a custom add, update the cart icon bubble count and
   * re-render the drawer contents from the server (same Section Rendering
   * mechanism Horizon uses internally).
   * @param {CustomEvent<{ cart?: { item_count?: number } }>} event
   */
  #handlePsCartSync = async (event) => {
    try {
      let itemCount = event?.detail?.cart?.item_count;
      if (typeof itemCount !== 'number') {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) itemCount = (await cartResponse.json()).item_count;
      }

      const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function' && typeof itemCount === 'number') {
        cartIcon.renderCartBubble(itemCount, false);
      }

      const drawerItems = this.querySelector('cart-items-component[data-drawer]');
      const sectionId = drawerItems instanceof HTMLElement ? drawerItems.dataset.sectionId : undefined;
      if (!sectionId) return;

      const response = await fetch(`/?sections=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const data = await response.json();
      const html = data?.[sectionId];
      if (!html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newItems = parsed.querySelector('cart-items-component[data-drawer]');
      const currentItems = document.querySelector('cart-items-component[data-drawer]');
      if (newItems && currentItems) currentItems.replaceWith(newItems);

      this.#applyPsCartTweaks();
    } catch (error) {
      console.error('ProSetup cart sync failed:', error);
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // ProSetup fix (v16): native cart-drawer auto-open is intentionally disabled
    // to prevent the intermittent empty-drawer flash that races with the custom
    // add-to-cart toast defined in snippets/prosetup-cart-ux.liquid.
    // The drawer still opens normally when the cart icon trigger is clicked (open()).
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
